import { ArrowDownIcon, ChevronRightIcon } from '@langchain/untitled-ui-icons';
import { CircularProgress } from '@mui/joy';

import { Dispatch, SetStateAction, useMemo } from 'react';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import {
  ComparisonViewColumn,
  ExamplesGroup,
  ExperimentViewOverridesSchema,
} from '@/types/schema';
import { cn } from '@/utils/tailwind';

import {
  ATTACHMENTS_COLUMN_IDX,
  DatasetSessionCompareSettings,
  FEEDBACK_COLUMNS_DEFAULT_WIDTH,
  INPUT_COLUMN_IDX,
  METRICS_COLUMNS,
  OUTPUT_START_COLUMN_IDX,
  REFERENCE_COLUMN_IDX,
} from '../../constants';
import {
  type ColumnRenderFunctions,
  type ExperimentViewMetric,
  renderOverriddenColumns,
} from '../../utils/renderOverriddenColumns';
import { ExperimentColumnHeader } from '../SingleExperimentColumns/ExperimentColumnHeader';
import { useSessionExtras } from '../hooks/useSessionExtras';
import { AggregatedStatHeader } from './AggregatedStatHeader';
import { SingleGroupMetricsColumnHeader } from './SingleGroupMetricsHeader';
import { useSingleGroup } from './useGroupByExamples';
import { useGroupByMetadata } from './useGroupByMetadata';
import { useGroupCollapse } from './useGroupCollapse';
import { useTableWidthStore } from './useTableFullWidth';
import { getKeyName } from './utils';

export const SingleGroupHeaderWithColumns = ({
  displaySettingsState,
  columns,
  columnWidthStorage,
  setColumnWidthStorage,

  sessionIds,
  sessionExtras,

  group,
  context,
  columnOverride,
}: {
  displaySettingsState: DatasetSessionCompareSettings;
  columns: ComparisonViewColumn[];
  sessionIds: string[];
  columnWidthStorage: number[];
  setColumnWidthStorage: Dispatch<SetStateAction<number[]>>;
  sessionExtras: ReturnType<typeof useSessionExtras>;
  group: ExamplesGroup;
  context: {
    datasetId?: string;
    sessionId: string;
    datasetShareToken?: string;
  };
  columnOverride?: ExperimentViewOverridesSchema['column_overrides'];
}) => {
  const { metadataKey: groupByMetadataKey } = useGroupByMetadata();
  const toggleCollapsed = useGroupCollapse((state) => state.toggleCollapsed);
  const collapsedStates = useGroupCollapse((state) => state.collapsedStates);

  const isGroupCollapsed = groupByMetadataKey
    ? collapsedStates[`${groupByMetadataKey.value}:${group.group_key}`]
    : false;

  const {
    isReferenceInputHidden,
    isReferenceOutputHidden,
    isAttachmentsHidden,

    hiddenFeedbackColumns,
    hiddenMetricsColumns,
  } = displaySettingsState;

  const { aggregateFeedbackKeys, feedbackRunFacets: _feedbackRunFacets } =
    sessionExtras;

  const feedbackRunFacets = _feedbackRunFacets.map((facet) => ({
    ...facet,
    avg: group.feedback_stats?.[facet.value]?.avg,
  }));

  const { datasetId, sessionId, datasetShareToken } = context ?? {};
  // we use the main header's table height to show the lines.
  const tableHeight = 0;
  // This is to trigger loading of single group  when header is rendered.
  useSingleGroup({
    datasetId,
    sessionId,
    group,
    datasetShareToken,
  });

  const displayedMetrics = Object.values(METRICS_COLUMNS).filter(
    (metric) => !hiddenMetricsColumns.includes(metric.name)
  );

  // Helper function to render individual feedback header
  const renderFeedbackHeader = (feedbackKey: string, columnIndex: number) => {
    if (hiddenFeedbackColumns.includes(feedbackKey)) return null;
    const facet = feedbackRunFacets.find(
      (facet) => facet.value === feedbackKey
    );
    return (
      <ExperimentColumnHeader
        key={feedbackKey}
        columnIndex={columnIndex}
        columnName={feedbackKey}
        tableHeight={tableHeight}
        columnWidthStorage={columnWidthStorage}
        setColumnWidthStorage={setColumnWidthStorage}
        minColumnWidth={FEEDBACK_COLUMNS_DEFAULT_WIDTH}
        className="flex h-[50px] items-center border-t-0"
        isTable={false}
      >
        <AggregatedStatHeader stat={Number(facet?.avg)} type={'AVG'} />
      </ExperimentColumnHeader>
    );
  };

  // Helper function to render individual metrics header
  const renderMetricsHeader = (
    metric: ExperimentViewMetric,
    columnIndex: number
  ) => (
    <SingleGroupMetricsColumnHeader
      key={metric.name}
      startingIndex={columnIndex}
      tableHeight={tableHeight}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      metric={metric}
      group={group}
      className="h-[50px] border-t-0"
      isTable={false}
    />
  );

  // Define header components
  const renderInputsHeader = (columnName: string, columnIndex: number) => {
    const inputHeader = (
      <ExperimentColumnHeader
        columnIndex={columnIndex}
        columnName={columnName}
        tableHeight={tableHeight}
        columnWidthStorage={columnWidthStorage}
        setColumnWidthStorage={setColumnWidthStorage}
        className="h-[50px] border-t-0"
        isTable={false}
      >
        {null}
      </ExperimentColumnHeader>
    );
    return inputHeader;
  };

  const renderAttachmentsHeader = (columnIndex: number) => {
    const attachmentsHeader = (
      <ExperimentColumnHeader
        columnIndex={columnIndex}
        columnName="Attachments"
        tableHeight={tableHeight}
        columnWidthStorage={columnWidthStorage}
        setColumnWidthStorage={setColumnWidthStorage}
        className="h-[50px] border-t-0"
        isTable={false}
      >
        {null}
      </ExperimentColumnHeader>
    );
    return attachmentsHeader;
  };

  const renderReferenceOutputsHeader = (
    columnName: string,
    columnIndex: number
  ) => {
    const referenceOutputHeader = (
      <ExperimentColumnHeader
        columnIndex={columnIndex}
        columnName={columnName}
        tableHeight={tableHeight}
        columnWidthStorage={columnWidthStorage}
        setColumnWidthStorage={setColumnWidthStorage}
        className="h-[50px] border-t-0"
        isTable={false}
      >
        {null}
      </ExperimentColumnHeader>
    );
    return referenceOutputHeader;
  };

  const renderDefaultOutputsHeader = (columnStartIndex: number) => {
    const outputsHeader = (
      <>
        {columns.map((_, idx) => {
          return (
            <ExperimentColumnHeader
              key={idx}
              columnIndex={columnStartIndex}
              columnName="Output"
              tableHeight={tableHeight}
              columnWidthStorage={columnWidthStorage}
              setColumnWidthStorage={setColumnWidthStorage}
              className="h-[50px] border-t-0"
              isTable={false}
            >
              {null}
            </ExperimentColumnHeader>
          );
        })}
      </>
    );
    return outputsHeader;
  };

  // Render computed/specific columns (inputs.field, outputs.field, etc.)
  const renderComputedColumnHeader = (
    columnName: string,
    columnIndex: number
  ) => (
    <ExperimentColumnHeader
      key={`header-${columnName}-${columnIndex}`}
      columnIndex={columnIndex}
      columnName={columnName}
      tableHeight={tableHeight}
      columnWidthStorage={columnWidthStorage}
      setColumnWidthStorage={setColumnWidthStorage}
      className="h-[50px] border-t-0"
      isTable={false}
    >
      {null}
    </ExperimentColumnHeader>
  );

  const feedbacksHeader = (
    <>
      {sessionIds.length === 1 &&
        aggregateFeedbackKeys.map((feedbackKey, idx) => {
          return renderFeedbackHeader(
            feedbackKey,
            OUTPUT_START_COLUMN_IDX + 1 + idx
          );
        })}
    </>
  );

  const metricsHeader = (
    <>
      {sessionIds.length === 1 &&
        displayedMetrics.map((metric, idx) =>
          renderMetricsHeader(
            metric,
            OUTPUT_START_COLUMN_IDX +
              aggregateFeedbackKeys.filter(
                (key) => !hiddenFeedbackColumns.includes(key)
              ).length +
              1 +
              idx
          )
        )}
    </>
  );

  const defaultHeader = (
    <>
      {!isReferenceInputHidden &&
        renderInputsHeader('Inputs', INPUT_COLUMN_IDX)}
      {!isAttachmentsHidden && renderAttachmentsHeader(ATTACHMENTS_COLUMN_IDX)}
      {!isReferenceOutputHidden &&
        renderReferenceOutputsHeader('Reference Outputs', REFERENCE_COLUMN_IDX)}
      {renderDefaultOutputsHeader(OUTPUT_START_COLUMN_IDX)}
      {feedbacksHeader}
      {metricsHeader}
    </>
  );

  // Generate headers based on configuration
  let configuredHeaders: React.ReactNode[] = [];
  if (columnOverride && sessionIds.length === 1) {
    const renderFunctions: ColumnRenderFunctions = {
      renderInputsColumn: renderInputsHeader,
      renderAttachmentsColumn: renderAttachmentsHeader,
      renderReferenceOutputsColumn: renderReferenceOutputsHeader,
      renderOutputsColumn: renderDefaultOutputsHeader,
      renderFeedbackColumn: renderFeedbackHeader,
      renderMetricsColumn: renderMetricsHeader,
      renderComputedColumn: renderComputedColumnHeader,
    };

    configuredHeaders = renderOverriddenColumns(
      columnOverride,
      {
        isReferenceInputHidden,
        isAttachmentsHidden,
        isReferenceOutputHidden,
        hiddenFeedbackColumns,
        hiddenMetricsColumns,
      },
      renderFunctions,
      aggregateFeedbackKeys,
      sessionIds.length === 1
    );
  }

  const categoryHeader = (
    <div
      className="flex h-full cursor-pointer items-center gap-2 bg-secondary px-4 py-2 hover:bg-tertiary"
      onClick={() => {
        if (!groupByMetadataKey) return;
        toggleCollapsed(groupByMetadataKey.value, group.group_key);
      }}
    >
      <span>
        {getKeyName(groupByMetadataKey)}:{' '}
        <pre className="inline-block bg-tertiary px-2">{group.group_key}</pre>
      </span>
      <ChevronRightIcon
        className={cn(
          !isGroupCollapsed && 'rotate-90',
          'size-4 transition-transform'
        )}
      />
    </div>
  );

  // Final header - either configured or default
  const finalHeader = useMemo(() => {
    const columnNameHeaders =
      configuredHeaders.length > 0 ? configuredHeaders : defaultHeader;
    return (
      <div className="flex flex-col">
        {categoryHeader}
        <div className="flex">{columnNameHeaders}</div>
      </div>
    );
  }, [configuredHeaders, defaultHeader]);

  return (
    <div
      className="flex flex-col bg-background"
      id={`group-header-${group.group_key}`}
    >
      <div className="flex">{finalHeader}</div>
    </div>
  );
};

export const SingleGroupLoadButton = ({
  context,
}: {
  context: {
    datasetId?: string;
    sessionId: string;
    datasetShareToken?: string;
    group: ExamplesGroup;
  };
}) => {
  const { datasetId, sessionId, datasetShareToken, group } = context ?? {};

  const fullWidth = useTableWidthStore((state) => state.fullWidth);
  const {
    hasMoreToLoad,
    currentPage,
    setExamplesSize,
    isNextPageValidating,
    examplesError,
  } = useSingleGroup({
    datasetId,
    sessionId,
    group,
    datasetShareToken,
    swrOptions: {
      revalidateOnMount: false,
    },
  });

  if (examplesError) {
    return (
      <div className="p-4">
        <ExpandableErrorAlert
          error={`Error fetching examples: ${examplesError.message}`}
        />
      </div>
    );
  }

  return (
    hasMoreToLoad && (
      <button
        type="button"
        className={cn(
          'relative z-10 -mt-[1px] flex w-full flex-1 cursor-pointer justify-center py-4',
          'border-y border-secondary bg-background transition-all hover:bg-secondary'
        )}
        onClick={() => setExamplesSize(currentPage + 1)}
        style={{ width: fullWidth ?? '100%' }}
      >
        <div className="flex items-center gap-2">
          {isNextPageValidating ? (
            <>
              <CircularProgress
                size="sm"
                color="neutral"
                sx={{ opacity: 0.5 }}
              />
              <span>Loading...</span>
            </>
          ) : (
            <>
              <span>
                Load more in{' '}
                <pre className="inline-block bg-tertiary px-2 font-semibold">
                  {group.group_key}
                </pre>
              </span>
              <ArrowDownIcon className="size-4" />
            </>
          )}
        </div>
      </button>
    )
  );
};
